import { languageJourneyLoaders } from "@/data/languageJourneyLoaders";
import manifest from "@/data/languageJourneyManifest.json";
import {
  type LanguageJourney,
  type LanguageJourneyCatalog,
  type LanguageJourneyChapter,
  type LanguageJourneyProgressMetadata,
  createLanguageJourneyApi,
} from "./language-journey/core";
import { createRetryableLoader } from "./retryable-loader";

export * from "./language-journey/core";

export const languageJourneyManifest = manifest;
const catalog: LanguageJourneyCatalog = {
  ...manifest,
  instructionLanguage: "English",
  languages: [],
};
const localized = new Map<string, LanguageJourney>();
const listeners = new Set<() => void>();
let revision = 0;
export const subscribeLanguageJourneys = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};
export const getLanguageJourneyRevision = () => revision;
export const getServerLanguageJourneyRevision = () => 0;
export function findLanguageJourneyMetadata(code: string | null | undefined) {
  if (!code) return null;
  return (
    manifest.languages.find((j) => j.languageCode === code) ||
    manifest.languages.find((j) =>
      (j as { alternateLanguageCodes?: string[] }).alternateLanguageCodes?.includes(code)
    ) ||
    manifest.languages.find((j) => j.languageCode.split("-")[0] === code) ||
    null
  );
}
const loads = new Map<string, () => Promise<void>>();
export function preloadLanguageJourney(code: string | null | undefined): Promise<void> {
  const metadata = findLanguageJourneyMetadata(code);
  if (!metadata) return Promise.resolve();
  let load = loads.get(metadata.slug);
  if (!load) {
    load = createRetryableLoader(async () => {
      const loader = languageJourneyLoaders[metadata.slug as keyof typeof languageJourneyLoaders];
      const journey = (await loader()) as LanguageJourney;
      let english: LanguageJourney | undefined;
      if (journey.progressNamespace === "english-es-v1") {
        const { localizeEnglishJourneyForDisplay } =
          await import("@/shared/english-journey-presentation");
        english = localizeEnglishJourneyForDisplay(journey, "en-US");
      }
      catalog.languages.push(journey);
      if (english) localized.set(journey.languageCode, english);
      revision++;
      listeners.forEach((listener) => listener());
    });
    loads.set(metadata.slug, load);
  }
  return load();
}
const progressMetadata = new Map<string, LanguageJourneyProgressMetadata>(
  manifest.languages.map((metadata) => [
    metadata.languageCode,
    {
      languageCode: metadata.languageCode,
      progressNamespace: (metadata as { progressNamespace?: string }).progressNamespace,
      chapters: metadata.launchChapters.map((chapter) => ({
        id: chapter[0] as string,
        chapterNumber: chapter[4] as number,
      })),
    },
  ])
);
const api = createLanguageJourneyApi(
  catalog,
  (journey, instruction) =>
    instruction?.toLowerCase().replace("_", "-").split("-")[0] === "es"
      ? journey
      : localized.get(journey.languageCode) || journey,
  (code) => {
    const metadata = findLanguageJourneyMetadata(code);
    return metadata ? progressMetadata.get(metadata.languageCode) || null : null;
  }
);
export const {
  LANGUAGE_JOURNEY_MAX_FUTURE_SKEW_MS,
  getLanguageJourneyCatalog,
  getLanguageJourneyByCode,
  getLanguageJourneyForInstructionLanguage,
  getLanguageJourneyChapter,
  getLanguageJourneyChaptersByLevel,
  getLanguageJourneyChapterGroups,
  getLanguageJourneyChapterGroupId,
  getLanguageJourneyChapterDisplayDescription,
  getLanguageJourneyChapterDisplayFocus,
  getLanguageJourneyChapterDisplayObjective,
  isLanguageJourneyChapterUnlocked,
  getLanguageJourneyProgressIdentityScope,
  getLanguageJourneyProgressNamespace,
  normalizeLanguageJourneyProgress,
  mergeLanguageJourneyProgress,
  loadLanguageJourneyProgress,
  saveLanguageJourneyProgress,
  markLanguageJourneyChapterComplete,
  markLanguageJourneyChapterIncomplete,
  syncLanguageJourneyProgress,
  loadAndSyncLanguageJourneyProgress,
  getLanguageJourneyCompletionPercent,
  getLanguageJourneyNextIncompleteChapter,
} = api;
export function getDefaultLanguageJourneyChapter(code: string | null | undefined) {
  return (
    api.getDefaultLanguageJourneyChapter(code) ||
    (findLanguageJourneyMetadata(code)?.firstChapter as LanguageJourneyChapter | undefined) ||
    null
  );
}

/** Minimal identity/access metadata; never pretends to contain learner copy. */
export function getLanguageJourneyLaunchChapter(
  code: string | null | undefined,
  chapterId?: string | null
) {
  const metadata = findLanguageJourneyMetadata(code);
  if (!metadata) return null;
  const chapters = metadata.launchChapters as Array<
    [string, boolean, string[], LanguageJourneyChapter["cefrLevel"], number]
  >;
  const match =
    chapters.find(([id]) => id === chapterId) ||
    chapters.find(([, , aliases]) => chapterId && aliases.includes(chapterId)) ||
    chapters.find((chapter) => String(chapter[4]) === chapterId) ||
    chapters[0];
  return match
    ? { id: match[0], isFreePreview: match[1], cefrLevel: match[3], chapterNumber: match[4] }
    : null;
}
